import React, {useState, useCallback} from "react";
import {useHistory} from "react-router";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PermContactCalendarIcon from "@material-ui/icons/PermContactCalendar";
import EventNoteIcon from "@material-ui/icons/EventNote";
import PeopleIcon from "@material-ui/icons/People";
import GroupIcon from "@material-ui/icons/Group";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import {Position, Tooltip} from "evergreen-ui";
import {ClientInfo, TeamContact} from "./domain";
import {formatDateStr} from "../../common/util/date";
import {fullName} from "../../core/domain";
import {EmailWithCopy} from "../../common";
import {useApplicationRootUrl} from "common/component/ApplicationRootContext";
import {ProviderPortalFeatureFlag, useFeatureFlag} from "featureflag";
import {CareCoordinationRequestModal} from "./care-coordination/CareCoordinationRequestModal";

// Static Catalight team mailboxes surfaced on the Care Services Team card. Declared once so the
// mailto href and the hover/copy address can never drift apart (BHPNPP-3107).
const CLIENT_PLACEMENT_EMAIL = 'clientplacement@catalight.org';
const OPERATIONS_AUTH_EMAIL = 'supportservices@catalight.org';

const CARE_COORDINATION_HELP =
    'Request that the internal Provider Case Manager team coordinate care with the other provider serving this client.';

function formatDualProviderServiceType(serviceType?: string | null): string {
    if (!serviceType) {
        return 'None';
    }
    const formatted = serviceType
        .split(';')
        .map((part) => part.trim())
        .filter(Boolean)
        .join(', ');
    return formatted || 'None';
}

function getStatusDisplay(status: string | null | undefined): { label: string; dotClass: string; bgClass: string } {
    if (!status || status === 'Not Requested') {
        return {label: 'Not Requested', dotClass: 'border-gray-400', bgClass: 'bg-gray-200 text-gray-600'};
    }
    if (['Completed', 'Closed', 'Resolved'].includes(status)) {
        return {label: 'Completed', dotClass: 'border-green-500', bgClass: 'bg-green-100 text-green-700'};
    }
    return {label: 'In Progress', dotClass: 'border-yellow-500', bgClass: 'bg-yellow-100 text-yellow-700'};
}

type CareCoordinationIndicatorProps = {
    otherProviderName?: string | null;
    requestStatus?: string | null;
    onRequestClick: () => void;
};

function CareCoordinationIndicator({otherProviderName, requestStatus, onRequestClick}: CareCoordinationIndicatorProps) {
    const statusDisplay = getStatusDisplay(requestStatus);
    return (
        <div className='flex items-center mr-4'>
            {otherProviderName &&
                <div className='flex items-center mr-4'>
                    <span className='text-xs font-bold text-atlantis-400 leading-tight text-right mr-2 whitespace-nowrap'>
                        Also receiving<br/>services with:
                    </span>
                    <span className='text-base font-bold text-blue-900 whitespace-nowrap'>{otherProviderName}</span>
                </div>
            }
            <button
                type='button'
                className='flex items-center bg-green-600 hover:bg-green-700 text-white rounded-md px-4 py-2 text-sm font-bold whitespace-nowrap outline-none focus:outline-none'
                onClick={onRequestClick}
            >
                Request Care Coordination
                <Tooltip content={CARE_COORDINATION_HELP} position={Position.BOTTOM}>
                    <span
                        className='ml-2 inline-flex items-center justify-center h-4 w-4 rounded-full border border-white text-xs font-bold leading-none'
                        aria-label={CARE_COORDINATION_HELP}
                    >
                        ?
                    </span>
                </Tooltip>
            </button>
            <span className={`ml-3 inline-flex items-center ${statusDisplay.bgClass} text-sm font-semibold rounded-full px-3 py-1 whitespace-nowrap`}>
                <span className={`h-2 w-2 rounded-full border-2 ${statusDisplay.dotClass} mr-2 box-border`}/>
                {statusDisplay.label}
            </span>
        </div>
    );
}

type ClientProfileTopBarProps = {
    clientInfo?: ClientInfo;
    teamContacts?: TeamContact[];
    expanded: boolean;
    onToggle: () => void;
    clientGuid: string;
    onRefresh?: () => void | Promise<unknown>;
}

type DemographicCellProps = {
    label: string;
    value?: string | null;
}

function DemographicCell({label, value}: DemographicCellProps) {
    return (
        <div className='py-1'>
            <div className='text-xs text-blue-900'>{label}</div>
            <div className='text-lg text-blue-900 mt-1'>{value ? value : 'None'}</div>
        </div>
    );
}

type CareCardProps = {
    icon: React.ReactNode;
    title: string;
    children: React.ReactNode;
}

function CareCard({icon, title, children}: CareCardProps) {
    return (
        <div className='border border-gray-300 rounded-lg p-4'>
            <div className='flex items-center mb-3'>
                <div className='flex items-center justify-center h-12 w-12 rounded-full bg-gray-200 text-blue-900 mr-4'>
                    {icon}
                </div>
                <span className='text-base font-semibold text-blue-900'>{title}</span>
            </div>
            {children}
        </div>
    );
}

type DualProviderDetailRowProps = {
    label: string;
    value: string;
    last?: boolean;
}

function DualProviderDetailRow({label, value, last = false}: DualProviderDetailRowProps) {
    return (
        <div className={`flex items-center justify-between py-2 ${last ? '' : 'border-b border-gray-200'}`}>
            <span className='text-sm text-blue-900'>{label}</span>
            <span className='text-sm font-semibold text-blue-900 text-right ml-4'>{value}</span>
        </div>
    );
}

export function ClientProfileTopBar({clientInfo, teamContacts, expanded, onToggle, clientGuid, onRefresh}: ClientProfileTopBarProps) {
    const history = useHistory();
    const rootUrl = useApplicationRootUrl();
    const isApEnabled = process.env.REACT_APP_AP_ENABLED === 'true';
    const dualProviderFlagEnabled = useFeatureFlag(ProviderPortalFeatureFlag.BHPNPP_3195_DUAL_PROVIDER_CARE_COORDINATION_ENABLED);

    const [showCareCoordModal, setShowCareCoordModal] = useState(false);

    const referralAcceptedDate = clientInfo?.referralAcceptedDate || null;
    const anticipatedDischargeDate = clientInfo?.providerAnticipatedDischargeDate?.anticipatedDischargeDate || null;
    const caseManager = (teamContacts || []).find((c) => c.memberRole === "CATALIGHT_CASE_MANAGER");
    const showDualProviderDetails = dualProviderFlagEnabled && Boolean(clientInfo?.dualProvider);
    const otherProviderName = clientInfo?.dualProviderOtherServices?.providerName || null;
    const otherProviderServiceType = formatDualProviderServiceType(clientInfo?.dualProviderOtherServices?.serviceType);
    const rawCareCoordRequestStatus = clientInfo?.latestCareCoordinationRequest?.status;
    const careCoordRequestStatus = !rawCareCoordRequestStatus
        ? 'Not Requested'
        : ['Completed', 'Closed', 'Resolved'].includes(rawCareCoordRequestStatus)
            ? 'Completed'
            : 'In Progress';

    const handleCareCoordSubmitSuccess = useCallback(async () => {
        await onRefresh?.();
    }, [onRefresh]);

    return (
        <div className='bg-white shadow-md rounded-lg px-5 py-4'>
            <div className='flex items-center justify-between flex-wrap'>
                <div className='flex items-center'>
                    <div className='flex items-center justify-center h-9 w-9 rounded-lg bg-gray-200 text-blue-900 mr-3'>
                        <PermContactCalendarIcon/>
                    </div>
                    <span className='text-xl font-semibold text-blue-900'>Client Details</span>
                    <span className='text-xl font-semibold text-red-marron ml-3 whitespace-nowrap'>{clientInfo?.fullName}</span>
                </div>
                <div className='flex items-center'>
                    {showDualProviderDetails &&
                        <CareCoordinationIndicator
                            otherProviderName={otherProviderName}
                            requestStatus={careCoordRequestStatus}
                            onRequestClick={() => setShowCareCoordModal(true)}
                        />
                    }
                    <button
                        className='flex items-center text-blue-portal-link outline-none focus:outline-none'
                        onClick={onToggle}
                    >
                        {expanded ? 'Hide details' : 'Show details'}
                        {expanded ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                    </button>
                    <div className='border-l border-gray-300 h-6 mx-4'/>
                    <button
                        className='flex items-center border border-blue-portal-link rounded-lg px-3 py-2 text-blue-portal-link whitespace-nowrap outline-none focus:outline-none'
                        onClick={() => history.push(`${rootUrl}/clients`)}
                    >
                        <ArrowBackIcon fontSize='small'/>
                        <span className='ml-1'>Back to client list</span>
                    </button>
                </div>
            </div>

            {expanded &&
                <div className='mt-5'>
                    <div className='grid grid-cols-6 gap-x-4'>
                        <DemographicCell label='Client Number' value={clientInfo?.bhpnClientId}/>
                        <DemographicCell label='Medical Record Number' value={clientInfo?.medicalRecordNumber}/>
                        <DemographicCell label='Date of Birth'
                                         value={clientInfo?.birthDate ? formatDateStr(clientInfo.birthDate) : null}/>
                        <DemographicCell label='Gender' value={clientInfo?.genderInfo}/>
                        <DemographicCell label='Preferred Language' value={clientInfo?.preferredLanguages?.join(", ")}/>
                        <DemographicCell label='Client Status' value={clientInfo?.status}/>
                    </div>

                    <div className={`grid gap-4 mt-5 ${showDualProviderDetails ? 'grid-cols-3' : 'grid-cols-2'}`}>
                        <CareCard icon={<EventNoteIcon fontSize='large'/>} title='Episode of Care Dates'>
                            {/* Interim (BHPNPP-3030): existing Referral Accepted Date; the full care-episode period list awaits a back-end source (SFC_CareEpisode). pl-16 aligns the rows under the title (past the icon column). */}
                            <div className='pl-16'>
                                <div className='border border-gray-300 rounded-lg px-3 py-2 text-sm text-blue-900'>
                                    Referral Accepted Date: {referralAcceptedDate && formatDateStr(referralAcceptedDate)}
                                </div>
                                {isApEnabled && anticipatedDischargeDate &&
                                    <div className='border border-gray-300 rounded-lg px-3 py-2 text-sm text-blue-900 mt-2'>
                                        Anticipated Discharge Date: {formatDateStr(anticipatedDischargeDate)}
                                    </div>
                                }
                            </div>
                        </CareCard>

                        <CareCard icon={<PeopleIcon fontSize='large'/>} title='Catalight Care Services Team'>
                            {caseManager &&
                                <div className='text-sm text-blue-900 mb-2 text-center'>
                                    Clinical Case Manager: {fullName(caseManager)}{caseManager.mobile ? ` * ${caseManager.mobile}` : ""}
                                </div>
                            }
                            <div className='flex items-center justify-center'>
                                <EmailWithCopy email={CLIENT_PLACEMENT_EMAIL}>
                                    <a href={`mailto:${CLIENT_PLACEMENT_EMAIL}`}
                                       className='flex items-center text-blue-portal-link'>
                                        Client Placement <MailOutlineIcon fontSize='small' className='ml-1'/>
                                    </a>
                                </EmailWithCopy>
                                <div className='border-l border-gray-300 h-5 mx-4'/>
                                <EmailWithCopy email={OPERATIONS_AUTH_EMAIL}>
                                    <a href={`mailto:${OPERATIONS_AUTH_EMAIL}`}
                                       className='flex items-center text-blue-portal-link'>
                                        Operations/Authorizations <MailOutlineIcon fontSize='small' className='ml-1'/>
                                    </a>
                                </EmailWithCopy>
                            </div>
                        </CareCard>

                        {showDualProviderDetails &&
                            <CareCard icon={<GroupIcon fontSize='large'/>} title='Also Receiving Services'>
                                <DualProviderDetailRow
                                    label='Provider Name'
                                    value={otherProviderName || 'None'}
                                />
                                <DualProviderDetailRow
                                    label='Service Type'
                                    value={otherProviderServiceType}
                                />
                                <DualProviderDetailRow
                                    label='Request Status'
                                    value={careCoordRequestStatus || 'Not Requested'}
                                    last
                                />
                            </CareCard>
                        }
                    </div>
                </div>
            }

            {showDualProviderDetails &&
                <CareCoordinationRequestModal
                    visible={showCareCoordModal}
                    onClose={() => setShowCareCoordModal(false)}
                    clientGuid={clientGuid}
                    otherProviderName={otherProviderName}
                    onSubmitSuccess={handleCareCoordSubmitSuccess}
                />
            }
        </div>
    );
}
